import React, { useEffect, useState } from 'react';
import { UserCheck } from 'lucide-react';
import { aaiApi } from '../../services/aaiApi';
import type { RequestAssignee } from '../../types/aai';
import { Action, Chip, ErrorNote } from '../../components/aa';

/*
 * Who is on a piece of work, and the two ways it moves: hand it to somebody
 * else, or step off it.
 *
 * This lived inside the Open requests list, which is the only screen that ever
 * had it. Every role's own UI opens a Project through its record panel instead,
 * and that panel carried no roster at all, so "name a scoper" existed in the
 * product and could not be found by anyone looking at a Project. It lives here
 * now and both screens draw the same one.
 *
 * "Nobody yet" is only worth saying once a decision has been taken. Before
 * that there is nothing to be on, and saying so on every pending row is noise.
 */

export type RosterRole = 'scoper' | 'builder';

const ROLE_COPY: Record<RosterRole, { one: string; none: string; add: string; title: string; note: string }> = {
  scoper: {
    one: 'is scoping it',
    none: 'Nobody is scoping this yet',
    add: 'Name a scoper',
    title: 'Who will scope it',
    note: 'A first request rarely says enough to build from. Whoever you name fills in the rest, and can hand it on themselves.',
  },
  builder: {
    one: 'is building it',
    none: 'Nobody is building this yet',
    add: 'Name a builder',
    title: 'Who will build it',
    note: 'They are told, and the requester can see who has it. The build document goes to them alone instead of to every expert.',
  },
};

const isDecided = (status: string): boolean => status === 'approved' || status === 'dept_approved';

const Person: React.FC<{
  person: RequestAssignee;
  role: RosterRole;
  canStaff: boolean;
  busy: boolean;
  onHand: (from: string) => void;
  onDrop: (email: string) => void;
}> = ({ person, role, canStaff, busy, onHand, onDrop }) => (
  <span className="inline-flex flex-wrap items-center gap-1.5 rounded-full bg-canvas-inset px-2.5 py-1 text-[12px] text-ink-dim">
    <UserCheck size={12} className="text-[#3F7A2F]" />
    <span className="text-ink">{person.email}</span> {ROLE_COPY[role].one}
    {canStaff && (
      <>
        <Action size="sm" variant="ghost" disabled={busy} onClick={() => onHand(person.email)}>Hand over</Action>
        <Action size="sm" variant="ghost" disabled={busy} onClick={() => onDrop(person.email)}>Remove</Action>
      </>
    )}
  </span>
);

const Line: React.FC<{
  role: RosterRole;
  people: RequestAssignee[];
  canStaff: boolean;
  busy: boolean;
  onAdd: () => void;
  onHand: (from: string) => void;
  onDrop: (email: string) => void;
}> = ({ role, people, canStaff, busy, onAdd, onHand, onDrop }) => {
  const copy = ROLE_COPY[role];
  if (people.length === 0) {
    return (
      <div className="flex flex-wrap items-center gap-2">
        <Chip tone="warn" dot>{copy.none}</Chip>
        {canStaff && <Action size="sm" variant="quiet" disabled={busy} onClick={onAdd}>{copy.add}</Action>}
      </div>
    );
  }
  return (
    <div className="flex flex-wrap items-center gap-2">
      {people.map((p) => (
        <Person key={p.email} person={p} role={role} canStaff={canStaff} busy={busy} onHand={onHand} onDrop={onDrop} />
      ))}
      {canStaff && <Action size="sm" variant="quiet" disabled={busy} onClick={onAdd}>Add another</Action>}
    </div>
  );
};

export const RequestRoster: React.FC<{
  requestId: string;
  status: string;
  scopers?: RequestAssignee[];
  builders?: RequestAssignee[];
  /** Naming a scoper is the department head's as well as the company's. */
  canStaffScoping: boolean;
  /** Naming a builder is the company gate alone. */
  canStaffBuilding: boolean;
  onChanged: () => void | Promise<void>;
}> = ({ requestId, status, scopers, builders, canStaffScoping, canStaffBuilding, onChanged }) => {
  const [experts, setExperts] = useState<{ email: string; name: string }[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [asking, setAsking] = useState<{ role: RosterRole; from: string | null } | null>(null);
  const [pick, setPick] = useState('');

  useEffect(() => {
    aaiApi.assignableExperts()
      .then((res) => setExperts(res.data.experts || []))
      .catch(() => setExperts([]));
  }, []);

  if (!isDecided(status)) return null;

  const close = () => { setAsking(null); setPick(''); };

  const save = async () => {
    if (!asking || !pick) return;
    setBusy(true);
    try {
      if (asking.from) {
        await aaiApi.transferRequestAssignee(requestId, { to_email: pick, role: asking.role, from_email: asking.from });
      } else {
        await aaiApi.addRequestAssignee(requestId, pick, asking.role);
      }
      setError(null);
      close();
      await onChanged();
    } catch (e: any) {
      // The refusal is the product here: a client naming one of the delivery
      // partner's people is told whose people they are and who allocates them.
      setError(e?.response?.data?.detail || 'Could not record who is on it.');
    } finally {
      setBusy(false);
    }
  };

  const drop = async (role: RosterRole, email: string) => {
    setBusy(true);
    try {
      await aaiApi.removeRequestAssignee(requestId, email, role);
      setError(null);
      await onChanged();
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Could not take them off.');
    } finally {
      setBusy(false);
    }
  };

  let title = ROLE_COPY[asking?.role || 'builder'].title;
  let note = ROLE_COPY[asking?.role || 'builder'].note;
  let confirm = 'Assign';
  if (asking?.from) {
    title = 'Hand it to somebody else';
    note = `${asking.from} steps off and whoever you pick takes it on. Both are kept on the record, so the handover is not lost.`;
    confirm = 'Hand over';
  }

  return (
    <div>
      <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.1em] text-ink-soft">Who is on it</div>
      {error && <div className="mb-2"><ErrorNote message={error} /></div>}
      <div className="space-y-2">
        <Line
          role="scoper"
          people={scopers || []}
          canStaff={canStaffScoping}
          busy={busy}
          onAdd={() => { setPick(''); setAsking({ role: 'scoper', from: null }); }}
          onHand={(from) => { setPick(''); setAsking({ role: 'scoper', from }); }}
          onDrop={(email) => drop('scoper', email)}
        />
        <Line
          role="builder"
          people={builders || []}
          canStaff={canStaffBuilding}
          busy={busy}
          onAdd={() => { setPick(''); setAsking({ role: 'builder', from: null }); }}
          onHand={(from) => { setPick(''); setAsking({ role: 'builder', from }); }}
          onDrop={(email) => drop('builder', email)}
        />
      </div>

      {asking && (
        <div className="fixed inset-0 z-[195] flex items-center justify-center bg-black/30 p-4" onClick={() => !busy && close()}>
          <div className="w-full max-w-sm rounded-panel bg-white p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <h2 className="m-0 text-[15px] font-semibold text-ink">{title}</h2>
            <p className="mt-1 text-[12.5px] leading-[20px] text-ink-dim">{note}</p>
            <select
              value={pick}
              onChange={(e) => setPick(e.target.value)}
              aria-label="Who takes it on"
              className="mt-4 w-full rounded-act border border-line px-3 py-2 text-[13px] text-ink"
            >
              <option value="">Pick someone</option>
              {experts.map((x) => <option key={x.email} value={x.email}>{x.name}</option>)}
            </select>
            {experts.length === 0 && (
              <p className="mt-3 text-[12px] leading-[19px] text-ink-dim">
                Nobody on the roster can take this yet. Add an Expert on the Team page first.
              </p>
            )}
            <div className="mt-4 flex justify-end gap-2">
              <Action size="sm" variant="quiet" disabled={busy} onClick={close}>Cancel</Action>
              <Action size="sm" disabled={busy || !pick} onClick={save}>{busy ? 'Saving' : confirm}</Action>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RequestRoster;
